import { PageHeader, SectionHeader } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import type { IPushHistory } from '@frontend/utils/';
import { FoundPatientStatus } from '@common/enums/';
import { patientSearchStartBarUpdateSchema } from '@common/validation/patient';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { serviceConsumersSelector } from '@frontend/store/serviceConsumers/serviceConsumerActions';
import { getSelectedLanguage } from '@frontend/translate';
import { historyEnabled, useAbortableFetch, validate } from '@frontend/utils/';
import type { IPatientInOrganization } from './searchPatient/PatientSearch';
import type { NewPatient } from './searchPatient/patientSearchContext';
import { ProcessStartWizardCard } from '../processStartWizard/components/ProcessStartWizardCard';
import { DuplicatePatientIdAlert } from './DuplicatePatientIdAlert';
import { PatientsList } from './PatientsList';
import { NoPatientResultBar } from './searchPatient/NoPatientResultBar';
import { PatientEnterExtraInformationBar } from './searchPatient/PatientEnterExtraInformationBar';
import { PatientResultBar } from './searchPatient/PatientResultBar';
import { PatientSearch } from './searchPatient/PatientSearch';
import { PatientSearchContext } from './searchPatient/patientSearchContext';
import { PatientSearchStartBar } from './searchPatient/PatientSearchStartBar';

export type PatientsListSortKeys = keyof Pick<
    IPatientInOrganization,
    'nameFamily' | 'identifierSocialSecurity' | 'birthDate' | 'lastNewInfoDateTime'
>;

export interface IPatientsHistoryProps {
    page?: number;
}

export interface IPatientsListFilters {
    limit: number;
    showOwnPatientsOnly: boolean;
    sortDirection: 'ASC' | 'DESC';
    sortKey: PatientsListSortKeys;
}

const limit = 10;

export function PatientsComponent(props: IPushHistory<IPatientsHistoryProps>) {
    const navigate = useNavigate();
    const abortableFetch = useAbortableFetch();
    const translate = getSelectedLanguage();

    const [canCreateAndSeeAllPatients, setCanCreateAndSeeAllPatients] = useState(false);
    const [filters, setFilters] = useState<IPatientsListFilters>({
        limit,
        showOwnPatientsOnly: true,
        sortDirection: 'ASC',
        sortKey: 'nameFamily',
    });
    const [isServiceProviderAndRequestCreator, setIsServiceProviderAndRequestCreator] = useState(false);
    const [hasStartingPoints, setHasStartingPoints] = useState(false);

    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const serviceConsumers = useSelector((state: IReduxState) => serviceConsumersSelector(state).serviceConsumers);
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);

    useEffect(() => {
        checkIsServiceProviderAndRequestCreator();
        checkHasStartingPoints();
    }, [selectedEmploymentId]);

    useEffect(() => {
        checkAccessProcessStartWizard();
    }, [isServiceProviderAndRequestCreator, serviceConsumers, hasStartingPoints]);

    if (isManager) {
        return null;
    }

    return (
        <PageTemplate>
            <PageHeader title={translate.patient.patients} />
            <PatientSearch patientsListFilters={filters}>
                <PatientSearchContext.Consumer>
                    {({ newPatient, foundPatientStatus, duplicatePatientId }) => (
                        <>
                            <ProcessStartWizardCard>
                                <PatientSearchStartBar
                                    canCreateAndSeeAllPatients={canCreateAndSeeAllPatients}
                                    header={true}
                                    isPatientsList={true}
                                    onStartUrl={() => '/patients/'}
                                    text={translate.patient.searchPatient}
                                />

                                {foundPatientStatus === FoundPatientStatus.extraInformationNeeded &&
                                    (canCreateAndSeeAllPatients ? (
                                        <PatientEnterExtraInformationBar />
                                    ) : (
                                        <NoPatientResultBar />
                                    ))}
                            </ProcessStartWizardCard>

                            {newPatient?.patient && (
                                <>
                                    <SectionHeader title={translate.patient.newPatient} />
                                    <ProcessStartWizardCard>
                                        <PatientResultBar
                                            disabled={Boolean(duplicatePatientId)}
                                            icon="profile"
                                            onStart={onStartClick}
                                            patient={newPatient.patient}
                                        />
                                        {duplicatePatientId && <DuplicatePatientIdAlert id={duplicatePatientId} />}
                                    </ProcessStartWizardCard>
                                </>
                            )}
                        </>
                    )}
                </PatientSearchContext.Consumer>

                <PatientsList
                    filters={filters}
                    setFilters={setFilters}
                    {...props}
                />
            </PatientSearch>
        </PageTemplate>
    );

    async function onStartClick(newPatient: NewPatient) {
        if (!newPatient) {
            return;
        }

        if (newPatient.patient.id) {
            navigate(`/patients/${newPatient.patient.id}`);
        } else {
            const { errors: validationErrors, item: validatedPatient } = validate(
                patientSearchStartBarUpdateSchema,
                newPatient.patient
            );

            if (validationErrors.size === 0) {
                const newPatientData = {
                    patient: { ...validatedPatient },
                };

                const result = await abortableFetch.fetchJson('/api/patients', 'POST', newPatientData);
                navigate(`/patients/${result.json.id}`);
            } else {
                const validationError = validationErrors.entries().next().value;
                throw new Error(
                    `SBVZ/Vecozo patient data validation failed. ${validationError[0]}: ${validationError[1]}`
                );
            }
        }
    }

    function checkAccessProcessStartWizard() {
        const hasAccessHomepage =
            serviceConsumers.length > 0 || isServiceProviderAndRequestCreator || hasStartingPoints;
        setCanCreateAndSeeAllPatients(hasAccessHomepage);
    }

    async function checkIsServiceProviderAndRequestCreator() {
        if (selectedEmploymentId) {
            const result = await abortableFetch.fetchJson(
                `/api/serviceProviders?employmentId=${selectedEmploymentId}&isActive=true&isRequestCreator=true`,
                'GET'
            );
            setIsServiceProviderAndRequestCreator(result.json.items.length > 0);
        } else {
            setIsServiceProviderAndRequestCreator(false);
        }
    }

    async function checkHasStartingPoints() {
        const url = '/api/services/serviceStartingPoints';
        const result = await abortableFetch.fetchJson(url, 'GET');
        setHasStartingPoints(result.json.length > 0);
    }
}

export const Patients = historyEnabled(PatientsComponent);
